/**
 * Live Italian menu demo for the salespage.
 *
 * Renders a real interactive restaurant menu (Antipasti / Primi / Secondi)
 * powered by the actual HustlePlate menu engine. Each dish has a hero
 * photograph generated by Gemini Nano Banana — no stock-photo placeholders.
 *
 * Includes a "tap any dish" interaction that opens a sheet with the
 * ingredients and dietary tags so the visitor can FEEL how their menu would
 * look. Followed by a 4-up photo gallery.
 */
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Utensils, ImageIcon, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0));

const DIET_LABEL = {
  vegan: "Vegan", vegetarian: "Veg", gf: "GF", gluten_free: "GF",
  df: "DF", dairy_free: "DF", nut_free: "Nut-free", halal: "Halal",
};

function ItemCard({ item, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`sp-menu-item-${item.slug}`}
      className="group text-left w-full flex gap-4 p-4 rounded-xl bg-white border border-stone-200/70 hover:border-stone-400 hover:shadow-md transition-all"
    >
      <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Utensils className="h-6 w-6 text-stone-300" strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="font-serif text-lg leading-tight text-stone-900 truncate">
            {item.title}
          </h4>
          <span className="font-medium text-stone-900 tabular-nums">{aud(item.price_aud)}</span>
        </div>
        {item.subtitle ? (
          <p className="text-sm text-stone-600 mt-1 line-clamp-2">{item.subtitle}</p>
        ) : null}
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {(item.dietary || []).slice(0, 3).map((d) => (
            <Badge
              key={d}
              variant="secondary"
              className="text-[10px] bg-emerald-50 text-emerald-700 font-medium px-1.5 py-0"
            >
              {DIET_LABEL[d] || d}
            </Badge>
          ))}
        </div>
      </div>
    </button>
  );
}

function GalleryStrip({ items }) {
  if (!items.length) return null;
  return (
    <div className="mt-12 pt-12 border-t border-stone-200/70">
      <div className="flex items-center gap-2 mb-5">
        <ImageIcon className="h-4 w-4 text-stone-500" strokeWidth={1.75} />
        <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">
          The cucina lookbook
        </span>
      </div>
      <h3 className="font-serif text-2xl text-stone-900 mb-6 max-w-xl">
        Every dish photographed in editorial style — built into the platform.
      </h3>
      <div
        data-testid="sp-menu-gallery"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
      >
        {items.map((g) => (
          <a
            key={g.slug}
            href={`/recipes/${g.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`sp-gallery-${g.slug}`}
            className="group relative overflow-hidden rounded-xl aspect-[4/5] bg-stone-100"
          >
            <img
              src={g.image_url}
              alt={g.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
              <div className="text-white text-sm font-medium leading-tight line-clamp-2">
                {g.title}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function SalesItalianMenuDemo() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetch(`${API}/sales/menu/italian`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <section className="sp-section sp-section--paper">
        <div className="sp-container">
          <p className="text-sm text-stone-400">Loading the cucina…</p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="menu-demo"
      className="sp-section sp-section--paper"
      data-testid="sales-italian-menu"
    >
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" strokeWidth={2} />
              Live menu · Italian trattoria template
            </div>
            <h2 className="sp-h">
              What your menu looks like when<br />it actually works on a phone.
            </h2>
            <p className="sp-lead">
              Tap a dish. See the photo, the price, the dietary tags, the
              ingredients. No PDF gymnastics, no pinch-zoom. The same menu
              your kitchen edits at lunch service shows up here in two
              seconds — across every device.
            </p>
          </div>
          <a
            href="/?biz=demo-trattoria"
            target="_blank"
            rel="noreferrer"
            className="sp-btn sp-btn--ghost"
            data-testid="sales-menu-demo-link"
          >
            Open the live demo →
          </a>
        </div>

        <div className="mt-10 space-y-12">
          {data.sections.map((sec) => (
            <div key={sec.key} data-testid={`sp-menu-section-${sec.key}`}>
              <div className="flex items-baseline justify-between mb-5">
                <h3 className="font-serif text-3xl text-stone-900">{sec.title}</h3>
                <div className="text-xs uppercase tracking-wider text-stone-400">
                  {sec.items.length} dishes
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sec.items.map((item) => (
                  <ItemCard key={item.slug} item={item} onClick={() => setActive(item)} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <GalleryStrip items={data.gallery || []} />
      </div>

      {/* Item detail sheet — replicates the live menu's "tap any dish" UX */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {active ? (
            <>
              <div className="-mx-6 -mt-6 mb-6">
                <div className="relative aspect-[4/3] w-full bg-stone-100">
                  {active.image_url ? (
                    <img
                      src={active.image_url}
                      alt={active.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Utensils className="h-10 w-10 text-stone-300" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
              </div>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl flex items-baseline justify-between gap-3">
                  <span>{active.title}</span>
                  <span className="text-base font-medium text-stone-700">
                    {aud(active.price_aud)}
                  </span>
                </SheetTitle>
                <SheetDescription className="text-stone-600">
                  {active.subtitle}
                </SheetDescription>
              </SheetHeader>
              {(active.dietary || []).length ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {active.dietary.map((d) => (
                    <Badge
                      key={d}
                      variant="secondary"
                      className="bg-emerald-50 text-emerald-700"
                    >
                      {DIET_LABEL[d] || d}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {(active.main_ingredients || []).length ? (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-medium mb-2">
                    Hero ingredients
                  </div>
                  <ul className="text-sm text-stone-700 space-y-1">
                    {active.main_ingredients.map((i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-stone-400">·</span>
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <a
                href={`/recipes/${active.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 block w-full text-center rounded-full bg-stone-900 hover:bg-stone-700 text-white py-3 font-medium transition-colors"
                data-testid="sp-menu-view-recipe"
              >
                See the full recipe →
              </a>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </section>
  );
}
